import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { supabase } from '../lib/supabase';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { theme } from '../styles/theme';

type Mode = 'password' | 'magic' | 'reset';

export function Login() {
  const { signInWithEmail, signInWithPassword, session } = useAuth();
  const [params] = useSearchParams();
  const redirect = params.get('redirect');
  const hadSessionOnMount = useRef<boolean | null>(null);

  // Default a magic link — más amigable para clientes migrados desde BC sin password
  const [mode, setMode] = useState<Mode>('magic');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [recognizedName, setRecognizedName] = useState<string | null>(null);
  const nav = useNavigate();

  // Solo redirigir si la session se creó AHORA (post-login),
  // no si el usuario llega a /login con session ya activa.
  useEffect(() => {
    if (hadSessionOnMount.current === null) {
      hadSessionOnMount.current = !!session;
      return;
    }
    if (session && !hadSessionOnMount.current) {
      nav(redirect ?? '/portal', { replace: true });
    }
  }, [session, redirect, nav]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    setLoading(true);
    if (mode === 'password') {
      const { error } = await signInWithPassword(email, password);
      setLoading(false);
      if (error) setErr(error.message);
      // si entra, useEffect redirige solo
    } else if (mode === 'reset') {
      // Restablecer contraseña — manda email con link a /reset-password
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: window.location.origin + '/reset-password',
      });
      setLoading(false);
      if (error) {
        setErr(error.message);
      } else {
        setSent(true);
      }
    } else {
      // Magic link — primero validar que reconocemos el email (cliente o staff)
      const { data: check } = await supabase.rpc('check_customer_email', { p_email: email });
      const isCustomer = check && (check as any).exists === true;
      const customerName = isCustomer ? (check as any).business_name : null;

      // Si NO es customer reconocido, también permitimos magic link (puede ser staff o nuevo)
      // pero damos mensaje diferente
      const { error } = await signInWithEmail(email);
      setLoading(false);
      if (error) {
        setErr(error.message);
      } else {
        setRecognizedName(customerName);
        setSent(true);
      }
    }
  };

  return (
    <div style={{ background: theme.bg, color: theme.text, fontFamily: theme.font.sans }}>
      <Header />
      <section style={{ maxWidth: 460, margin: '60px auto', padding: 24 }}>
        <div style={{
          background: theme.bg,
          border: `1px solid ${theme.border}`,
          padding: 32,
        }}>
          <div style={{
            fontSize: 11,
            letterSpacing: 2,
            textTransform: 'uppercase',
            color: theme.accentDark,
            fontWeight: 700,
            marginBottom: 8,
          }}>
            Acceso distribuidores
          </div>
          <h1 style={{
            fontFamily: theme.font.head,
            fontSize: 24,
            fontWeight: 400,
            color: theme.brand,
            margin: '0 0 24px',
          }}>
            Inicia sesión
          </h1>

          {/* Toggle entre password y magic link */}
          <div style={{
            display: 'flex',
            border: `1px solid ${theme.border}`,
            marginBottom: 20,
          }}>
            <ToggleBtn active={mode === 'password'} onClick={() => { setMode('password'); setSent(false); setErr(null); }}>
              Contraseña
            </ToggleBtn>
            <ToggleBtn active={mode === 'magic'} onClick={() => { setMode('magic'); setSent(false); setErr(null); }}>
              Magic link
            </ToggleBtn>
            <ToggleBtn active={mode === 'reset'} onClick={() => { setMode('reset'); setSent(false); setErr(null); }}>
              Restablecer
            </ToggleBtn>
          </div>

          {sent && (mode === 'magic' || mode === 'reset') ? (
            <div style={{
              border: `1px solid ${theme.accent}`,
              background: theme.accentSoft,
              padding: 16,
              fontSize: 14,
              color: theme.brand,
              lineHeight: 1.5,
            }}>
              {mode === 'reset' ? (
                <>
                  <div style={{ fontWeight: 700, marginBottom: 6 }}>
                    Revisa tu correo.
                  </div>
                  <div>
                    Te enviamos un link a <strong>{email}</strong> para que establezcas
                    una contraseña nueva. Si no llega en 1 minuto, revisa Spam.
                  </div>
                </>
              ) : recognizedName ? (
                <>
                  <div style={{ fontWeight: 700, marginBottom: 6 }}>
                    Hola, {recognizedName}.
                  </div>
                  <div>
                    Hemos actualizado tu portal. Revisa <strong>{email}</strong> y haz click en el link para entrar.
                    Tus precios y pedidos anteriores siguen intactos.
                  </div>
                </>
              ) : (
                <div>
                  Revisa <strong>{email}</strong> y haz click en el link para entrar.
                  Si no llega en 1 minuto, revisa Spam.
                </div>
              )}
            </div>
          ) : (
            <form onSubmit={submit}>
              <Field label="Email">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="tu@empresa.com"
                  autoComplete="username"
                  style={inputStyle}
                />
              </Field>

              {mode === 'password' && (
                <div style={{ marginTop: 14 }}>
                  <Field label="Contraseña">
                    <input
                      type="password"
                      required
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="••••••••"
                      autoComplete="current-password"
                      style={inputStyle}
                    />
                  </Field>
                </div>
              )}

              {mode === 'magic' && (
                <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 8, lineHeight: 1.5 }}>
                  Recibirás un magic link por correo. Una vez dentro, puedes
                  establecer tu contraseña desde <strong>Mi cuenta → Seguridad</strong>
                  {' '}para entrar más rápido la próxima vez.
                </div>
              )}

              {mode === 'password' && (
                <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 8, lineHeight: 1.5 }}>
                  Si es tu primera vez aquí, usa el <strong>magic link</strong> arriba.
                  ¿Olvidaste tu contraseña? Usa <strong>Restablecer</strong>.
                </div>
              )}

              {mode === 'reset' && (
                <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 8, lineHeight: 1.5 }}>
                  Te enviamos un link a tu correo para establecer una contraseña nueva.
                  Funciona también si nunca habías establecido una.
                </div>
              )}

              {err && (
                <div style={{
                  color: theme.err,
                  fontSize: 13,
                  marginTop: 12,
                  padding: 10,
                  background: 'rgba(220,38,38,0.05)',
                  border: `1px solid ${theme.err}`,
                }}>
                  {err}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                style={{
                  marginTop: 16,
                  width: '100%',
                  background: theme.accent,
                  color: theme.brand,
                  border: 'none',
                  padding: '14px 16px',
                  fontSize: 13,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: 0.4,
                  cursor: loading ? 'wait' : 'pointer',
                  opacity: loading ? 0.7 : 1,
                  fontFamily: theme.font.sans,
                }}
              >
                {loading ? 'Procesando…'
                  : mode === 'password' ? 'Entrar'
                  : mode === 'reset' ? 'Enviar link de restablecimiento'
                  : 'Enviar magic link'}
              </button>

              {mode === 'password' && (
                <div style={{ textAlign: 'center', marginTop: 12, display: 'flex', justifyContent: 'center', gap: 16 }}>
                  <button
                    type="button"
                    onClick={() => setMode('magic')}
                    style={linkBtn}
                  >
                    Magic link
                  </button>
                  <span style={{ color: theme.border }}>·</span>
                  <button
                    type="button"
                    onClick={() => setMode('reset')}
                    style={linkBtn}
                  >
                    Restablecer contraseña
                  </button>
                </div>
              )}
            </form>
          )}

          <div style={{
            marginTop: 24,
            paddingTop: 24,
            borderTop: `1px solid ${theme.border}`,
            fontSize: 13,
            color: theme.textMuted,
          }}>
            ¿Aún no eres distribuidor?{' '}
            <Link to="/distribuidor" style={{ color: theme.accentDark, fontWeight: 700, textDecoration: 'none' }}>
              Vuélvete distribuidor
            </Link>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}

function ToggleBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        flex: 1,
        background: active ? theme.brand : theme.bg,
        color: active ? theme.textOnBrand : theme.brand,
        border: 'none',
        padding: '10px 14px',
        fontSize: 12,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: 0.4,
        cursor: 'pointer',
        fontFamily: theme.font.sans,
      }}
    >
      {children}
    </button>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'block' }}>
      <div style={{
        fontSize: 11,
        color: theme.textMuted,
        marginBottom: 6,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        fontWeight: 700,
      }}>
        {label}
      </div>
      {children}
    </label>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  background: theme.bg,
  border: `1px solid ${theme.border}`,
  padding: '12px 14px',
  color: theme.text,
  fontSize: 14,
  fontFamily: theme.font.sans,
  outline: 'none',
  boxSizing: 'border-box',
};

const linkBtn: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: theme.textMuted,
  fontSize: 12,
  cursor: 'pointer',
  textDecoration: 'underline',
  fontFamily: theme.font.sans,
};
